import { AnchorLink, BestSelect, BestTable, Checkbox, DatePickerInput, Input } from "best-common-react";
import React, { useContext, useEffect, useMemo } from "react";
import { useHistory } from "react-router";
import styled from "styled-components";
import { LookupApi } from "../../api/LookupApi";
import PartnerLeagueApi from "../../api/PartnerLeagueApi";
import { RouteConstants } from "../../constants/RouteConstants";
import { useAuth } from "../../contexts/AuthContext";
import PersonPartnerHistoryContext, { PersonPartnerHistoryActions } from "../../contexts/history/history.context";
import { Column } from "../../models/best-common-react/tables.component";
import { PersonPartnerHistory } from "../../models/history/history.models";
import { Lookup } from "../../models/lookups/lookups.models";
import { formatDate, ShortDate } from "../../modules/dates/dates";
import { LargeAndLargerView, MediumAndSmallerView } from "../../modules/view/views";
import PartnerTable from "./../common/partner-table.component";

interface Props {
    currentLeagueId: number;
}

const RangeCheckboxWrapper = styled.div`
    .bcr-checkbox-wrapper {
        float: right;
    }
`;
const TableWrapper = styled.div`
    & .bcr-table-header {
        background: ${props => props.theme["LighterBlue"]};
    }
    &. bcr-header-cell {
        color: ${props => props.theme["Dark"]};
    }
`;
const GrayItalisized = styled.div`
    font-size: 12px;
    font-style: italic;
    color: ${props => props.theme["Gray"]};
`;

const HistoryList = styled.div`
    overflow: auto;
    max-height: 73vh;
`;

const HistoryItem = styled.div`
    background-color: #ffffff;
    border: 1px solid ${props => props.theme["LighterBlue"]};
    display: flex;
    justify-content: space-between;
    margin-bottom: 2px;
    padding: 12px;
`;

interface HistoryDisplayInfo {
    date: Date;
    displayName: string;
    positionCode: string;
    leagueName: string;
    clubName: string;
    description: string;
    playerId: number;
}

const ALL_LEAGUES_OPTION: Lookup = { id: "0", value: "All Leagues", label: "All Leagues" };

const ALL_TEAMS_OPTION: Lookup = { id: "0", value: "All Teams", label: "All Teams" };

function prependAllTeamsOption(clubs: Lookup[]): Lookup[] {
    const next = [...clubs];
    next.splice(0, 0, ALL_TEAMS_OPTION);
    return next;
}

function buildClubSelectOptions(
    clubsByLeagueId: Record<number, Lookup[]>,
    leagueFilterId: number | null | undefined,
): Lookup[] {
    if (leagueFilterId !== null && leagueFilterId !== undefined) {
        const clubs = clubsByLeagueId[leagueFilterId] ?? [];
        return prependAllTeamsOption([...clubs]);
    }

    const seen = new Set<string>();
    const merged: Lookup[] = [];
    Object.keys(clubsByLeagueId).forEach(key => {
        const leagueNum = Number(key);
        const list = clubsByLeagueId[leagueNum] ?? [];
        for (const c of list) {
            const idKey = String(c.id);
            if (!seen.has(idKey)) {
                seen.add(idKey);
                merged.push(c);
            }
        }
    });
    merged.sort((a, b) =>
        String(a.label ?? a.value).localeCompare(String(b.label ?? b.value), undefined, { sensitivity: "base" }),
    );
    return prependAllTeamsOption(merged);
}

function leagueDropdownValue(leagueLookup: Lookup[] | undefined, leagueId: number | null | undefined): Lookup {
    const opts = leagueLookup ?? [];
    if (leagueId === null || leagueId === undefined) {
        return opts.find(o => String(o.id) === "0") ?? ALL_LEAGUES_OPTION;
    }
    return opts.find(l => Number(l.id) === leagueId) ?? ALL_LEAGUES_OPTION;
}

interface Props {
    currentLeagueId: number;
}

const History = ({ currentLeagueId }: Props): JSX.Element => {
    const { state, dispatchHistory } = useContext(PersonPartnerHistoryContext);
    const { partnerUserDetails } = useAuth();

    const browserHistory = useHistory();

    const clubSelectOptions = useMemo(() => buildClubSelectOptions(state.clubsByLeagueId ?? {}, state.form.leagueId), [
        state.clubsByLeagueId,
        state.form.leagueId,
    ]);

    const leagueSelectValue = useMemo(() => leagueDropdownValue(state.leagueLookup, state.form.leagueId), [
        state.leagueLookup,
        state.form.leagueId,
    ]);

    const isBocUser = useMemo(() => partnerUserDetails?.isBoc ?? false, [partnerUserDetails]);
    const userLeagueId = useMemo(() => partnerUserDetails?.leagueIds?.[0], [partnerUserDetails]);

    useEffect(() => {
        if (!partnerUserDetails) return;
        Promise.all([LookupApi.getLookups("PARTNER_ACTIVITY_TYPES"), LookupApi.getLookups("PARTNER_LEAGUE")]).then(
            async ([lookups, partnerLeagueLookups]) => {
                const partnerLeagues = partnerLeagueLookups["PARTNER_LEAGUE"] ?? [];
                const clubPairs = await Promise.all(
                    partnerLeagues.map(lk =>
                        LookupApi.getParentLookup("PARTNER_LEAGUE_CLUB", Number(lk.id)).then(
                            clubs => [Number(lk.id), clubs] as [number, Lookup[]],
                        ),
                    ),
                );
                const clubsByLeagueId: Record<number, Lookup[]> = {};
                clubPairs.forEach(([id, clubs]) => {
                    clubsByLeagueId[id] = clubs;
                });

                const leagueLookup: Array<Lookup> = [ALL_LEAGUES_OPTION, ...partnerLeagues];

                let initialLeagueFilter: number | null;
                if (isBocUser) {
                    initialLeagueFilter = null;
                } else if (userLeagueId !== undefined && userLeagueId !== null) {
                    initialLeagueFilter = userLeagueId;
                } else if (partnerLeagues[0] !== undefined) {
                    initialLeagueFilter = Number(partnerLeagues[0].id);
                } else {
                    initialLeagueFilter = null;
                }

                dispatchHistory({
                    type: PersonPartnerHistoryActions.LOOKUPS_LOADED,
                    activityLookup: lookups["PARTNER_ACTIVITY_TYPES"],
                    selectedActivity: lookups["PARTNER_ACTIVITY_TYPES"].find(lk => Number(lk.id) === 1),
                    clubsByLeagueId,
                    selectedClub: ALL_TEAMS_OPTION,
                    leagueLookup,
                    formLeagueId: initialLeagueFilter,
                });
            },
        );
    }, [partnerUserDetails, isBocUser, userLeagueId, dispatchHistory]);

    useEffect(() => {
        if (currentLeagueId !== null && currentLeagueId !== undefined) {
            dispatchHistory({
                type: PersonPartnerHistoryActions.LEAGUE_CHANGED,
                selectedLeague: { id: String(currentLeagueId), value: "All Leagues" },
            });
        }
    }, [currentLeagueId, dispatchHistory]);

    useEffect(() => {
        if (state.formLoaded) {
            PartnerLeagueApi.getHistory(state.form).then(history => {
                console.log("history: ", history);
                dispatchHistory({
                    type: PersonPartnerHistoryActions.HISTORY_CHANGED,
                    history: history,
                });
            });
        }
    }, [state.form, state.formLoaded, dispatchHistory]);

    const getHistoryItem = (history: PersonPartnerHistory): JSX.Element => {
        return (
            <HistoryItem key={history.personPartnerHistoryId}>
                <div>
                    <div>
                        <AnchorLink
                            onClick={() =>
                                browserHistory.push(
                                    `${RouteConstants.PLAYER_PROFILE.replace(":playerId", String(history.playerId))}`,
                                )
                            }
                        >
                            {history.displayName}
                        </AnchorLink>{" "}
                        - <span>{history.positionCode} </span>
                    </div>
                    {history.leagueName ? <GrayItalisized>{history.leagueName}</GrayItalisized> : null}
                    <GrayItalisized>{history.clubName}</GrayItalisized>
                    <div>{history.description}</div>
                </div>
                <div>
                    <GrayItalisized>
                        <ShortDate value={history.activityDt} />
                    </GrayItalisized>
                </div>
            </HistoryItem>
        );
    };

    const PlayerFormatter = ({ row, format }) => {
        const route = RouteConstants.PLAYER_PROFILE.replace(":playerId", row.playerId);
        return <AnchorLink onClick={() => browserHistory.push(`${route}`)}>{row.displayName}</AnchorLink>;
    };

    const columns: Array<Column> = [
        { name: "Date", key: "date", sortable: true },
        { name: "Player", key: "displayName", formatter: PlayerFormatter, sortable: true },
        { name: "Pos", key: "positionCode", sortable: true },
        { name: "League", key: "leagueName", sortable: true },
        { name: "Club", key: "clubName", sortable: true },
        { name: "Description", key: "description", sortable: true },
    ];

    const renderHistoryRows = (historyData): Array<HistoryDisplayInfo> => {
        if (historyData !== null && historyData !== undefined && historyData.length > 0) {
            return historyData.map(hist => {
                return {
                    date: formatDate(hist.activityDt, "MM/DD/YYYY - hh:mma"),
                    displayName: hist.displayName,
                    positionCode: hist.positionCode,
                    leagueName: hist.leagueName ?? "",
                    clubName: hist.clubName,
                    description: hist.description === "Request Accepted" ? "Request Submitted" : hist.description,
                    playerId: hist.playerId,
                };
            });
        }
        return [];
    };

    return (
        <div>
            <div className="d-flex row mt-2">
                <RangeCheckboxWrapper className="col-lg-4 col-md-6 mr-sm-4">
                    <Checkbox
                        label="range"
                        onChange={event => {
                            dispatchHistory({
                                type: PersonPartnerHistoryActions.FORM_FIELD_CHANGED,
                                property: "range",
                                value: event.target.checked,
                            });
                        }}
                        checked={state.form.range}
                    />
                </RangeCheckboxWrapper>
            </div>
            <div className="d-flex row mx-lg-2 mx-0 mb-4">
                {state.form.range ? (
                    <>
                        <div className="col-lg-2 col-md-3 pt-lg-0 pt-md-0 pt-sm-2 px-0 px-lg-3">
                            <DatePickerInput
                                value={state.form.dateFrom}
                                onChange={newVal => {
                                    dispatchHistory({
                                        type: PersonPartnerHistoryActions.FORM_FIELD_CHANGED,
                                        property: "dateFrom",
                                        value: newVal,
                                    });
                                }}
                            />
                        </div>
                        <div className="col-lg-2 col-md-3 pt-lg-0 pt-md-0 pt-sm-2 px-0 px-lg-3">
                            <DatePickerInput
                                value={state.form.dateTo}
                                onChange={newVal => {
                                    dispatchHistory({
                                        type: PersonPartnerHistoryActions.FORM_FIELD_CHANGED,
                                        property: "dateTo",
                                        value: newVal,
                                    });
                                }}
                            />
                        </div>
                    </>
                ) : (
                    <div className="col-lg-4 col-md-6 pt-lg-0 pt-md-0 pt-sm-2 px-0 px-lg-3">
                        <DatePickerInput
                            value={state.form.selectedDate}
                            onChange={newVal => {
                                dispatchHistory({
                                    type: PersonPartnerHistoryActions.FORM_FIELD_CHANGED,
                                    property: "selectedDate",
                                    value: newVal,
                                });
                            }}
                        />
                    </div>
                )}
                <div className="col-lg-2 col-md-6 pt-lg-0 pt-md-0 pt-sm-2 px-0 px-lg-3">
                    <BestSelect
                        value={state.selectedActivity !== null ? state.selectedActivity : ""}
                        placeholder="All Activity"
                        onChange={newVal => {
                            dispatchHistory({
                                type: PersonPartnerHistoryActions.ACTIVITY_CHANGED,
                                selectedActivity: newVal,
                            });
                        }}
                        options={state.activityLookup}
                    />
                </div>
                <div className="col-lg-2 col-md-6 pt-lg-0 pt-md-3 pt-sm-2 px-0 px-lg-3">
                    <BestSelect
                        value={state.formLoaded ? leagueSelectValue : ""}
                        placeholder="League"
                        onChange={newVal => {
                            dispatchHistory({
                                type: PersonPartnerHistoryActions.LEAGUE_CHANGED,
                                selectedLeague: newVal,
                            });
                        }}
                        options={state.leagueLookup}
                    />
                </div>
                <div className="col-lg-2 col-md-6 pt-lg-0 pt-md-3 pt-sm-2 px-0 px-lg-3">
                    <BestSelect
                        value={state.selectedClub !== null ? state.selectedClub : ""}
                        placeholder="All Teams"
                        onChange={newVal => {
                            dispatchHistory({
                                type: PersonPartnerHistoryActions.CLUB_CHANGED,
                                selectedClub: newVal,
                            });
                        }}
                        options={clubSelectOptions}
                    />
                </div>
                <div className="col-lg-2 col-md-6 pt-lg-0 pt-md-3 pt-sm-2 px-0 px-lg-3">
                    <Input
                        type="text"
                        placeholder="type to search history"
                        value={state.form.searchText !== null ? state.form.searchText : ""}
                        onChange={event => {
                            dispatchHistory({
                                type: PersonPartnerHistoryActions.FORM_FIELD_CHANGED,
                                property: "searchText",
                                value: event.target.value,
                            });
                        }}
                    />
                </div>
            </div>
            <GrayItalisized className="mx-lg-4">{state.history.length} items</GrayItalisized>
            <LargeAndLargerView>
                <TableWrapper className="mx-4 thin-scroll">
                    <PartnerTable
                        data={renderHistoryRows(state.history)}
                        columns={columns}
                        defaultSort="date"
                        sortOrder={BestTable.DESC}
                    />
                </TableWrapper>
            </LargeAndLargerView>
            <MediumAndSmallerView>
                <HistoryList>{state.history.map(getHistoryItem)}</HistoryList>
            </MediumAndSmallerView>
        </div>
    );
};

export default History;
